import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { LlmToolSummary } from "@/types/api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiCheckList } from "@/components/multi-check-list";
import { CheckboxGroup } from "@/components/checkbox-group";
import { TagInput } from "@/components/tag-input";
import { RepeatableList } from "@/components/repeatable-list";
import { FieldExposure } from "@/components/field-exposure";
import {
  EMAIL_REPLY_MODES,
  EXPOSABLE_FIELD_KEYS,
  MANAGED_CONFIG_KEYS,
  MESSAGE_TYPES,
  type ConfigBlock,
  type ConfigBlockField,
  type ConfigBlockValue,
  type FirstMessageMode,
  type InstanceEmployeeConfig,
  type InstanceIntegration,
  type RuntimeModelPolicy,
  type VoiceSettings,
} from "@/types/employee-config";

/** Channel groups shown in the "Connect tools and communication channels" block. */
const CHANNEL_GROUPS = [
  { type: "chat", labelKey: "configBuilder.channels.groups.chat" },
  { type: "voice", labelKey: "configBuilder.channels.groups.voice" },
  { type: "email", labelKey: "configBuilder.channels.groups.email" },
] as const;

const NONE = "__none__";

/** Drop keys whose value is undefined / "" / empty array (keeps 0, false). */
function clean<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === undefined || v === "" || (Array.isArray(v) && v.length === 0))
      continue;
    out[k] = v;
  }
  return out as T;
}

interface WorkflowRow {
  key: string;
  steps: string;
}

/**
 * Structured "constructor" for an instance-employee `config`. Renders one
 * editor per section of InstanceEmployeeConfig and generates the config
 * object; any unknown key present in `config` is preserved untouched.
 * Mount only once the employee is loaded (parent gates on `initialized`).
 *
 * The layout is employee-type dependent: some fields only make sense for
 * certain types (e.g. greeting + email greeting/signature are shown for
 * `email_responder`, not for every bot — those are shared AI-bot settings on
 * the instance otherwise).
 */
/** One card of the config builder — lets the parent place each under a tab. */
export type ConfigBuilderSection =
  | "email"
  | "behaviour"
  | "runtime"
  | "voice"
  | "channels";

export function InstanceEmployeeConfigBuilder({
  config,
  onChange,
  disabled,
  employeeType,
  instanceName,
  section,
}: {
  config: InstanceEmployeeConfig;
  onChange: (config: InstanceEmployeeConfig) => void;
  disabled?: boolean;
  employeeType: string;
  /** Instance slug — enables fetching the instance's real integrations. */
  instanceName?: string;
  /** When set, render only this section's card (for a tabbed layout). */
  section?: ConfigBuilderSection;
}) {
  const { t } = useTranslation("instances");
  const isEmailResponder = employeeType === "email_responder";
  // No `section` prop → render every card (backward compatible).
  const show = (s: ConfigBuilderSection): boolean => !section || section === s;
  const toolsQuery = useQuery({
    queryKey: ["llm-tools-summaries"],
    queryFn: () =>
      api.get<{ tools: LlmToolSummary[] }>("/api/admin/llm-tools/summaries"),
  });

  // Real integrations set up on the instance (proxied by provisioning).
  const integrationsQuery = useQuery({
    queryKey: ["instance", instanceName, "integrations"],
    queryFn: () =>
      api.get<{ integrations: InstanceIntegration[]; unreachable?: boolean }>(
        `/api/admin/instances/${instanceName}/integrations`
      ),
    enabled: !!instanceName,
  });

  // Read helpers
  const str = (k: string) =>
    typeof config[k] === "string" ? (config[k] as string) : "";
  const arr = (k: string) =>
    Array.isArray(config[k]) ? (config[k] as string[]) : [];
  const rmp = (config.runtimeModelPolicy ?? {}) as RuntimeModelPolicy;

  const setKey = (key: string, value: unknown) => {
    const next: InstanceEmployeeConfig = { ...config };
    const isEmpty =
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value as object).length === 0);
    if (isEmpty) delete next[key];
    else next[key] = value;
    onChange(next);
  };

  const setRmp = (patch: Partial<RuntimeModelPolicy>) =>
    setKey("runtimeModelPolicy", clean({ ...rmp, ...patch }));

  // Workflows need a row representation (Record<string,string[]>) with
  // local state so a blank in-progress row doesn't vanish on each render.
  const [workflowRows, setWorkflowRows] = React.useState<WorkflowRow[]>(() =>
    Object.entries(
      (config.workflows ?? {}) as Record<string, string[]>
    ).map(([key, steps]) => ({ key, steps: (steps ?? []).join("\n") }))
  );
  const applyWorkflows = (rows: WorkflowRow[]) => {
    setWorkflowRows(rows);
    const record: Record<string, string[]> = {};
    for (const r of rows) {
      const k = r.key.trim();
      if (!k) continue;
      record[k] = r.steps
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
    }
    setKey("workflows", record);
  };

  const toolOptions = (toolsQuery.data?.tools ?? []).map((tool) => ({
    value: tool.toolId,
    label: tool.toolId,
    hint: tool.name,
  }));

  const preservedKeys = Object.keys(config).filter(
    (k) => !MANAGED_CONFIG_KEYS.includes(k)
  );

  const num = (v: string): number | undefined =>
    v === "" ? undefined : Number(v);

  // ── Voice settings (operator-only voice-engine configuration) ─────────────
  // Empty inputs delete their key and empty nested objects are dropped, so
  // an untouched section leaves config.voiceSettings absent entirely.
  const vs: VoiceSettings = config.voiceSettings ?? {};

  const setVoiceSettings = (patch: Partial<VoiceSettings>) =>
    setKey("voiceSettings", clean({ ...vs, ...patch }));
  const advancedJsonInvalid = React.useMemo(() => {
    const raw = vs.advancedOverridesJson;
    if (typeof raw !== "string" || raw.trim() === "") return false;
    try {
      JSON.parse(raw);
      return false;
    } catch {
      return true;
    }
  }, [vs.advancedOverridesJson]);

  // ── Per-field tenant exposure ("Show in user UI") ──────────────────────────
  const exposedFields = arr("tenantEditableFields");
  const isExposed = (fieldKey: string) => exposedFields.includes(fieldKey);
  const toggleExposed = (fieldKey: string, on: boolean) => {
    const set = new Set(exposedFields);
    if (on) set.add(fieldKey);
    else set.delete(fieldKey);
    setKey("tenantEditableFields", [...set]);
  };
  const expose = (fieldKey: string, label: string) => (
    <FieldExposure
      exposed={isExposed(fieldKey)}
      onToggle={(on) => toggleExposed(fieldKey, on)}
      disabled={disabled}
      label={label}
    />
  );

  // ── Channel (integration) selection ────────────────────────────────────────
  const selectedIntegrationIds = arr("channelIntegrationIds");
  const toggleIntegration = (id: string, on: boolean) => {
    const set = new Set(selectedIntegrationIds);
    if (on) set.add(id);
    else set.delete(id);
    setKey("channelIntegrationIds", [...set]);
  };
  const integrations = integrationsQuery.data?.integrations ?? [];
  const channelUnreachable = integrationsQuery.data?.unreachable === true;

  // ── Custom settings blocks (config.blocks) ─────────────────────────────────
  // The operator edits each field's `default` here: for tenant-editable
  // blocks it is the tenant's starting value, for operator-owned blocks
  // (tenantEditable: false) it IS the effective value injected into the
  // prompt. Block/field structure itself is authored on the template
  // (migration 0029) — this editor adjusts values and ownership.
  const configBlocks = Array.isArray(config.blocks)
    ? (config.blocks as ConfigBlock[])
    : [];

  const updateBlock = (index: number, patch: Partial<ConfigBlock>) => {
    const blocks = configBlocks.map((b, i) =>
      i === index ? { ...b, ...patch } : b
    );
    setKey("blocks", blocks);
  };

  const updateBlockFieldDefault = (
    blockIndex: number,
    fieldIndex: number,
    value: ConfigBlockValue | undefined
  ) => {
    const block = configBlocks[blockIndex];
    if (!block) return;
    const fields = block.fields.map((f, i) => {
      if (i !== fieldIndex) return f;
      if (value === undefined || value === null || value === "") {
        const { default: _omit, ...rest } = f;
        return rest as ConfigBlockField;
      }
      return { ...f, default: value };
    });
    updateBlock(blockIndex, { fields });
  };

  const renderBlockFieldDefault = (
    _block: ConfigBlock,
    blockIndex: number,
    field: ConfigBlockField,
    fieldIndex: number
  ) => {
    const set = (value: ConfigBlockValue | undefined) =>
      updateBlockFieldDefault(blockIndex, fieldIndex, value);
    switch (field.type) {
      case "textarea":
        return (
          <Textarea
            value={typeof field.default === "string" ? field.default : ""}
            onChange={(e) => set(e.target.value)}
            placeholder={field.placeholder}
            rows={3}
            disabled={disabled}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            value={typeof field.default === "number" ? field.default : ""}
            onChange={(e) =>
              set(e.target.value === "" ? undefined : Number(e.target.value))
            }
            placeholder={field.placeholder}
            disabled={disabled}
          />
        );
      case "boolean":
        return (
          <Switch
            checked={field.default === true}
            onCheckedChange={(c) => set(c)}
            disabled={disabled}
            aria-label={field.label}
          />
        );
      case "select":
        return (
          <Select
            value={
              typeof field.default === "string" && field.default !== ""
                ? field.default
                : NONE
            }
            onValueChange={(v) => set(v === NONE ? undefined : v)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              {(field.options ?? []).map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        return (
          <Input
            value={typeof field.default === "string" ? field.default : ""}
            onChange={(e) => set(e.target.value)}
            placeholder={field.placeholder}
            disabled={disabled}
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Email settings — only for the email responder */}
      {isEmailResponder && show("email") && (
        <Card>
          <CardHeader>
            <CardTitle>{t("configBuilder.email.title")}</CardTitle>
            <CardDescription>
              {t("configBuilder.email.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5 sm:max-w-xs">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="cfg-reply-mode">{t("configBuilder.email.replyMode")}</Label>
                {expose(EXPOSABLE_FIELD_KEYS.emailReplyMode, t("configBuilder.email.replyMode"))}
              </div>
              <Select
                value={str("emailReplyMode") || "manual"}
                onValueChange={(v) => setKey("emailReplyMode", v)}
                disabled={disabled}
              >
                <SelectTrigger id="cfg-reply-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">
                    {t("configBuilder.email.modeManual")}
                  </SelectItem>
                  <SelectItem value="draft">
                    {t("configBuilder.email.modeDraft")}
                  </SelectItem>
                  <SelectItem value="auto_send">
                    {t("configBuilder.email.modeAutoSend")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Human hand-off — NOT email-only. `escalate_to_human` is a generic
          tool every bot type (chat / voice / email) can call, and the hand-off
          is a silent no-op while this pool is empty: EscalationService picks no
          target, the session keeps no human assignee, and the employee-resolution
          gate lets the bot go on replying. So the exposure toggle has to be
          reachable for chat and voice bots too, not just the email responder. */}
      {(isEmailResponder ? show("email") : show("behaviour")) && (
        <Card>
          <CardHeader>
            <CardTitle>{t("configBuilder.handoff.title")}</CardTitle>
            <CardDescription>
              {t("configBuilder.handoff.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label>{t("configBuilder.handoff.team")}</Label>
                {expose(
                  EXPOSABLE_FIELD_KEYS.escalationTargets,
                  t("configBuilder.handoff.team"),
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t("configBuilder.handoff.hint")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Behavior — hidden for the email responder */}
      {!isEmailResponder && show("behaviour") && (
      <Card>
        <CardHeader>
          <CardTitle>{t("configBuilder.behaviour.title")}</CardTitle>
          <CardDescription>{t("configBuilder.behaviour.description")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5 sm:max-w-xs">
            <div className="flex items-center justify-between gap-2">
              <Label>{t("configBuilder.behaviour.emailReplyMode")}</Label>
              {expose(EXPOSABLE_FIELD_KEYS.emailReplyMode, t("configBuilder.behaviour.emailReplyMode"))}
            </div>
            <Select
              value={str("emailReplyMode") || NONE}
              onValueChange={(v) => setKey("emailReplyMode", v === NONE ? "" : v)}
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("configBuilder.behaviour.manual")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("configBuilder.behaviour.manual")}</SelectItem>
                {EMAIL_REPLY_MODES.map((m) => (
                  <SelectItem key={m} value={m}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {!isEmailResponder && (
            <div className="space-y-1.5">
              {/* No tenant exposure: the tenant editor has no renderer for
                  workflows — see EXPOSABLE_FIELD_KEYS. */}
              <Label>{t("configBuilder.behaviour.workflows")}</Label>
              <RepeatableList<WorkflowRow>
                items={workflowRows}
                onChange={applyWorkflows}
                newItem={() => ({ key: "", steps: "" })}
                addLabel={t("configBuilder.behaviour.addWorkflow")}
                emptyLabel={t("configBuilder.behaviour.noWorkflows")}
                disabled={disabled}
                renderRow={(row, update) => (
                  <div className="space-y-2">
                    <Input
                      value={row.key}
                      onChange={(e) => update({ key: e.target.value })}
                      placeholder={t("configBuilder.behaviour.workflowKeyPlaceholder")}
                      disabled={disabled}
                      className="h-9 font-mono text-xs"
                    />
                    <Textarea
                      value={row.steps}
                      onChange={(e) => update({ steps: e.target.value })}
                      placeholder={t("configBuilder.behaviour.stepsPlaceholder")}
                      rows={3}
                      disabled={disabled}
                    />
                  </div>
                )}
              />
            </div>
          )}

          {!isEmailResponder && (
            <div className="space-y-1.5">
              {/* No tenant exposure: no tenant-side renderer for custom
                  rules — see EXPOSABLE_FIELD_KEYS. */}
              <Label>{t("configBuilder.behaviour.customRules")}</Label>
              <RepeatableList<{ title: string; body: string }>
                items={
                  (config.customRules ?? []) as Array<{
                    title: string;
                    body: string;
                  }>
                }
                onChange={(v) => setKey("customRules", v)}
                newItem={() => ({ title: "", body: "" })}
                addLabel={t("configBuilder.behaviour.addRule")}
                emptyLabel={t("configBuilder.behaviour.noRules")}
                disabled={disabled}
                renderRow={(rule, update) => (
                  <div className="space-y-2">
                    <Input
                      value={rule.title}
                      onChange={(e) => update({ title: e.target.value })}
                      placeholder={t("configBuilder.behaviour.ruleTitle")}
                      disabled={disabled}
                      className="h-9"
                    />
                    <Textarea
                      value={rule.body}
                      onChange={(e) => update({ body: e.target.value })}
                      placeholder={t("configBuilder.behaviour.ruleBody")}
                      rows={2}
                      disabled={disabled}
                    />
                  </div>
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Runtime */}
      {show("runtime") && (
      <Card>
        <CardHeader>
          <CardTitle>{t("configBuilder.runtime.title")}</CardTitle>
          <CardDescription>
            {t("configBuilder.runtime.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            {/* No tenant exposure: tools stay operator-only — the tenant
                editor has no renderer for toolIds (see EXPOSABLE_FIELD_KEYS). */}
            <Label>{t("configBuilder.runtime.tools")}</Label>
            {toolsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">{t("configBuilder.runtime.loadingTools")}</p>
            ) : (
              <MultiCheckList
                options={toolOptions}
                selected={arr("toolIds")}
                onChange={(v) => setKey("toolIds", v)}
                disabled={disabled}
                emptyLabel={t("configBuilder.runtime.noTools")}
              />
            )}
          </div>

          {isEmailResponder ? (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="cfg-read-attachments">{t("configBuilder.runtime.readAttachments")}</Label>
                {expose(EXPOSABLE_FIELD_KEYS.readAttachments, t("configBuilder.runtime.readAttachments"))}
              </div>
              <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                  {t("configBuilder.runtime.readAttachmentsHint")}
                </p>
                <Switch
                  id="cfg-read-attachments"
                  checked={config.readAttachments === true}
                  onCheckedChange={(c) => setKey("readAttachments", c)}
                  disabled={disabled}
                  aria-label={t("configBuilder.runtime.readAttachments")}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {/* No tenant exposure: no tenant-side renderer for message
                  types — see EXPOSABLE_FIELD_KEYS. */}
              <Label>{t("configBuilder.runtime.messageTypes")}</Label>
              <CheckboxGroup
                options={MESSAGE_TYPES.map((m) => ({ value: m, label: m }))}
                selected={arr("supportedMessageTypes")}
                onChange={(v) => setKey("supportedMessageTypes", v)}
                disabled={disabled}
              />
            </div>
          )}

          {/* No tenant exposure: model policy stays operator-only — the
              tenant editor has no renderer for it (see EXPOSABLE_FIELD_KEYS). */}
          <Label className="text-muted-foreground">{t("configBuilder.runtime.modelPolicy")}</Label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cfg-provider">{t("configBuilder.runtime.modelProvider")}</Label>
              <Input
                id="cfg-provider"
                value={rmp.provider ?? ""}
                onChange={(e) => setRmp({ provider: e.target.value })}
                placeholder="openai"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfg-model">{t("configBuilder.runtime.model")}</Label>
              <Input
                id="cfg-model"
                value={rmp.model ?? ""}
                onChange={(e) => setRmp({ model: e.target.value })}
                placeholder="gpt-4.1"
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfg-temp">{t("configBuilder.runtime.temperature")}</Label>
              <Input
                id="cfg-temp"
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={rmp.temperature ?? ""}
                onChange={(e) => setRmp({ temperature: num(e.target.value) })}
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfg-maxtok">{t("configBuilder.runtime.maxOutputTokens")}</Label>
              <Input
                id="cfg-maxtok"
                type="number"
                min={1}
                value={rmp.max_output_tokens ?? ""}
                onChange={(e) =>
                  setRmp({ max_output_tokens: num(e.target.value) })
                }
                disabled={disabled}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>{t("configBuilder.runtime.fallbackModels")}</Label>
            <TagInput
              value={rmp.fallback_models ?? []}
              onChange={(v) => setRmp({ fallback_models: v })}
              placeholder={t("configBuilder.runtime.fallbackPlaceholder")}
              disabled={disabled}
            />
          </div>
        </CardContent>
      </Card>
      )}

      {/* Voice settings — operator-only voice-engine configuration */}
      {show("voice") && (
      <Card>
        <CardHeader>
          <CardTitle>{t("configBuilder.voice.title")}</CardTitle>
          <CardDescription>
            {t("configBuilder.voice.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* No tenant exposure: voice settings stay operator-only — the
              tenant editor has no renderer for them (see EXPOSABLE_FIELD_KEYS).
              Voice/transcriber are intentionally not operator-configurable. */}
          <Label className="text-muted-foreground">{t("configBuilder.voice.callBehavior")}</Label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="cfg-voice-first-mode">{t("configBuilder.voice.firstReplyMode")}</Label>
              <Select
                value={vs.firstMessageMode ?? NONE}
                onValueChange={(v) =>
                  setVoiceSettings({
                    firstMessageMode:
                      v === NONE ? undefined : (v as FirstMessageMode),
                  })
                }
                disabled={disabled}
              >
                <SelectTrigger id="cfg-voice-first-mode">
                  <SelectValue placeholder={t("configBuilder.voice.engineDefault")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("configBuilder.voice.engineDefault")}</SelectItem>
                  <SelectItem value="assistant-speaks-first">
                    {t("configBuilder.voice.speaksFirst")}
                  </SelectItem>
                  <SelectItem value="assistant-waits-for-user">
                    {t("configBuilder.voice.waitsForCaller")}
                  </SelectItem>
                  <SelectItem value="assistant-speaks-first-with-model-generated-message">
                    {t("configBuilder.voice.generatedOpening")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfg-voice-background">{t("configBuilder.voice.backgroundSound")}</Label>
              <Select
                value={vs.backgroundSound ?? NONE}
                onValueChange={(v) =>
                  setVoiceSettings({
                    backgroundSound:
                      v === NONE
                        ? undefined
                        : (v as VoiceSettings["backgroundSound"]),
                  })
                }
                disabled={disabled}
              >
                <SelectTrigger id="cfg-voice-background">
                  <SelectValue placeholder={t("configBuilder.voice.engineDefault")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t("configBuilder.voice.engineDefault")}</SelectItem>
                  <SelectItem value="off">{t("configBuilder.voice.backgroundOff")}</SelectItem>
                  <SelectItem value="office">{t("configBuilder.voice.backgroundOffice")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfg-voice-max-duration">
                {t("configBuilder.voice.maxDuration")}
              </Label>
              <Input
                id="cfg-voice-max-duration"
                type="number"
                min={10}
                max={7200}
                value={vs.maxDurationSeconds ?? ""}
                onChange={(e) =>
                  setVoiceSettings({ maxDurationSeconds: num(e.target.value) })
                }
                disabled={disabled}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cfg-voice-silence-timeout">
                {t("configBuilder.voice.silenceTimeout")}
              </Label>
              <Input
                id="cfg-voice-silence-timeout"
                type="number"
                min={10}
                max={600}
                value={vs.silenceTimeoutSeconds ?? ""}
                onChange={(e) =>
                  setVoiceSettings({
                    silenceTimeoutSeconds: num(e.target.value),
                  })
                }
                disabled={disabled}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cfg-voice-end-message">{t("configBuilder.voice.endCallMessage")}</Label>
            <Input
              id="cfg-voice-end-message"
              value={vs.endCallMessage ?? ""}
              onChange={(e) =>
                setVoiceSettings({ endCallMessage: e.target.value })
              }
              maxLength={500}
              disabled={disabled}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cfg-voice-advanced">
              {t("configBuilder.voice.advancedOverrides")}
            </Label>
            <Textarea
              id="cfg-voice-advanced"
              value={vs.advancedOverridesJson ?? ""}
              onChange={(e) =>
                setVoiceSettings({ advancedOverridesJson: e.target.value })
              }
              rows={4}
              disabled={disabled}
              className="font-mono text-xs"
            />
            {advancedJsonInvalid && (
              <p className="text-xs text-destructive">{t("configBuilder.voice.invalidJson")}</p>
            )}
            <p className="text-xs text-muted-foreground">
              {t("configBuilder.voice.advancedHint")}
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Connect tools and communication channels — real instance integrations */}
      {show("channels") && (
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <CardTitle>{t("configBuilder.channels.title")}</CardTitle>
            {expose(EXPOSABLE_FIELD_KEYS.channels, t("configBuilder.channels.exposeLabel"))}
          </div>
          <CardDescription>
            {t("configBuilder.channels.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!instanceName ? (
            <p className="text-sm text-muted-foreground">
              {t("configBuilder.channels.saveFirst")}
            </p>
          ) : integrationsQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">
              {t("configBuilder.channels.loading")}
            </p>
          ) : integrationsQuery.isError ? (
            <p className="text-sm text-destructive">
              {t("configBuilder.channels.loadFailed")}
            </p>
          ) : channelUnreachable ? (
            <p className="text-sm text-destructive">
              {t("configBuilder.channels.unreachable")}
            </p>
          ) : integrations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("configBuilder.channels.empty")}
            </p>
          ) : (
            <div className="space-y-4">
              {CHANNEL_GROUPS.map((group) => {
                const items = integrations.filter((i) => i.type === group.type);
                if (items.length === 0) return null;
                return (
                  <div key={group.type} className="space-y-1.5">
                    <Label>{t(group.labelKey)}</Label>
                    <div className="space-y-1.5">
                      {items.map((it) => {
                        const active = selectedIntegrationIds.includes(it.id);
                        return (
                          <label
                            key={it.id}
                            className={cn(
                              "flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm",
                              active
                                ? "border-primary bg-primary/5"
                                : "border-input",
                              disabled && "cursor-not-allowed opacity-50"
                            )}
                          >
                            <input
                              type="checkbox"
                              className="h-4 w-4"
                              checked={active}
                              onChange={(e) =>
                                toggleIntegration(it.id, e.target.checked)
                              }
                              disabled={disabled}
                            />
                            <span className="font-medium">{it.name}</span>
                            <span className="text-xs text-muted-foreground">
                              {it.provider}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* Custom settings blocks — per-block ownership + operator values */}
      {configBlocks.length > 0 && show("behaviour") && (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <CardTitle>{t("configBuilder.customSettings.title")}</CardTitle>
              {expose(EXPOSABLE_FIELD_KEYS.configBlockValues, t("configBuilder.customSettings.title"))}
            </div>
            <CardDescription>
              {t("configBuilder.customSettings.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {configBlocks.map((block, blockIndex) => (
              <div key={block.id} className="space-y-3 rounded-md border p-4">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-medium">{block.title}</p>
                    {block.description && (
                      <p className="text-xs text-muted-foreground">
                        {block.description}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Label
                      htmlFor={`cfg-block-editable-${block.id}`}
                      className="text-xs font-normal text-muted-foreground"
                    >
                      {t("configBuilder.customSettings.tenantCanEdit")}
                    </Label>
                    <Switch
                      id={`cfg-block-editable-${block.id}`}
                      checked={block.tenantEditable !== false}
                      onCheckedChange={(on) =>
                        updateBlock(blockIndex, { tenantEditable: on })
                      }
                      disabled={disabled}
                      aria-label={t("configBuilder.customSettings.tenantCanEdit")}
                    />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  {block.fields.map((field, fieldIndex) => (
                    <div key={field.key} className="space-y-1.5">
                      <Label>
                        {field.label}
                        {field.required && (
                          <span className="text-destructive"> *</span>
                        )}
                      </Label>
                      {renderBlockFieldDefault(
                        block,
                        blockIndex,
                        field,
                        fieldIndex
                      )}
                      {field.description && (
                        <p className="text-xs text-muted-foreground">
                          {field.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {preservedKeys.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {t("configBuilder.preservedKeys", { keys: preservedKeys.join(", ") })}
        </p>
      )}
    </div>
  );
}
